// Bu dosya, tüm API isteklerini yönetmek için merkezi bir nokta olacak.
import { getAuthToken } from './authHelper';
import debugLogger from './debugLogger';

// Production'da aynı origin'i kullan, development'ta localhost'a düş
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ||
  (window.location.hostname === 'localhost' ? 'http://localhost:3000' : window.location.origin);

// Token'ı localStorage'dan al (Supabase session formatı) - Fallback
const getAuthTokenFromStorage = () => {
  try {
    // Supabase'in varsayılan localStorage anahtarını kontrol et
    const keys = Object.keys(localStorage).filter(key => 
      key.startsWith('sb-') && key.includes('-auth-token')
    );
    
    for (const key of keys) {
      try {
        const sessionData = localStorage.getItem(key);
        if (sessionData) {
          const parsed = JSON.parse(sessionData);
          // Supabase session yapısını kontrol et
          if (parsed.access_token) {
            return parsed.access_token;
          }
          // Alternatif yapı
          if (parsed.session?.access_token) {
            return parsed.session.access_token;
          }
        }
      } catch (e) {
        // Bu anahtar geçersiz, devam et
        continue;
      }
    }
    
    // Fallback: tüm localStorage'ı tara
    for (const key of Object.keys(localStorage)) {
      if (key.includes('supabase') || key.includes('auth')) {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          if (data?.access_token) {
            return data.access_token;
          }
          if (data?.session?.access_token) {
            return data.session.access_token;
          }
        } catch (e) {
          // Devam et
        }
      }
    }
  } catch (error) {
    debugLogger.warn('Token al�n�rken hata:', error);
  }
  return null;
};

// Standart bir fetch fonksiyonunu sarmalayan (wrapper) bir fonksiyon
const api = async (endpoint, options = {}) => {
  // endpoint'in başına '/' ekle, eğer yoksa
  const url = `${API_BASE_URL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
  
  // Debug log
  debugLogger.apiRequest(endpoint, { ...options, url });

  // Varsayılan başlıkları ayarla
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Authentication token'ı ekle
  let token = null;
  try {
    token = await getAuthToken();
  } catch {}
  if (!token) {
    // Fallback: localStorage tara
    token = getAuthTokenFromStorage();
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const config = {
    ...options,
    headers,
  };

  // FormData gönderiyorsak Content-Type başlığını silmeliyiz, tarayıcı kendi ayarlar.
  if (config.body instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  const response = await fetch(url, config);

  // Yanıtın JSON olup olmadığını kontrol et
  const contentType = response.headers.get("content-type");
  let responseData;
  if (contentType && contentType.indexOf("application/json") !== -1) {
    responseData = await response.json();
  } else {
    // JSON değilse, metin olarak al (örn: 'Sipariş silindi' gibi mesajlar)
    responseData = { message: await response.text() };
  }
  
  // Debug log response
  debugLogger.apiResponse(endpoint, response, responseData);

  if (!response.ok) {
    // Debug log error
    debugLogger.apiError(endpoint, new Error(`HTTP ${response.status}: ${response.statusText}`), response);
    
    // Hata mesajını backend'den gelen detaylı bilgiyle zenginleştir
    let errorMessage = `HTTP hata! Durum: ${response.status}`;
    
    if (responseData && typeof responseData === 'object') {
      // Farklı hata formatlarını güvenli şekilde destekle
      errorMessage = responseData.details || 
                    responseData.error || 
                    responseData.message || 
                    responseData.error_message ||
                    errorMessage;
    } else if (typeof responseData === 'string') {
      errorMessage = responseData;
    }
    
    const error = new Error(errorMessage);
    // Backend'den gelen yanıtı hataya ekleyelim ki daha sonra kullanabilelim.
    error.response = responseData;
    error.status = response.status;
    error.responseData = responseData; // Ek güvenlik için
    throw error;
  }

  return responseData;
};

// Kolay kullanım için yardımcı metodlar ekliyoruz.
api.get = (endpoint, options = {}) => {
  // GET isteklerinde önbelleği önlemek için başlıklar ekle
  const headers = {
    ...options.headers,
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  };
  return api(endpoint, { ...options, headers, method: 'GET' });
};

api.post = (endpoint, body, options = {}) => {
  return api(endpoint, { ...options, method: 'POST', body: body instanceof FormData ? body : JSON.stringify(body) });
};

api.put = async (endpoint, body, options = {}) => {
  try {
    // PUT isteği gönderilmeden önce hata ayıklama bilgisi
    debugLogger.debug(`PUT iste�i g�nderiliyor: ${endpoint}`, body);

    // FormData kontrolü - FormData ise direkt gönder
    if (body instanceof FormData) {
      debugLogger.debug('FormData detected, sending directly');
      return api(endpoint, { 
        ...options, 
        method: 'PUT', 
        body: body // FormData'yı direkt gönder, JSON.stringify yapma!
      });
    }

    // İstek boyutunu küçültmek için gereksiz alanları çıkaralım
    const cleanBody = { ...body };

    // 'delivered' durumu için özel işleme
    if (cleanBody.status === 'delivered' && cleanBody.skip_updated_at) {
      delete cleanBody.skip_updated_at; // Bu alan sunucuya gönderilmemeli
      // Burada sadece durum bilgisi gönderilsin
      return api(endpoint, { 
        ...options, 
        method: 'PUT', 
        body: JSON.stringify({ status: 'delivered' }) 
      });
    }

    // Normal PUT isteği (JSON)
    return api(endpoint, { 
      ...options, 
      method: 'PUT', 
      body: JSON.stringify(cleanBody) 
    });
  } catch (error) {
    debugLogger.error(`PUT iste�i ba�ar�s�z: ${endpoint}`, error);
    throw error;
  }
};

api.patch = (endpoint, body, options = {}) => {
  return api(endpoint, { 
    ...options, 
    method: 'PATCH', 
    body: body instanceof FormData ? body : JSON.stringify(body) 
  });
};

api.delete = (endpoint, options = {}) => {
  return api(endpoint, { ...options, method: 'DELETE' });
};


export default api;
